
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import authService from '../services/authService';

// Tipos de acciones de autenticación
const ACCIONES_AUTH = {
    INICIO_LOGIN: 'INICIO_LOGIN',
    LOGIN_EXITOSO: 'LOGIN_EXITOSO',
    LOGIN_FALLIDO: 'LOGIN_FALLIDO',
    CERRAR_SESION: 'CERRAR_SESION',
    CARGAR_USUARIO: 'CARGAR_USUARIO',
    ACTUALIZAR_USUARIO: 'ACTUALIZAR_USUARIO',
    INICIO_REGISTRO: 'INICIO_REGISTRO',
    REGISTRO_EXITOSO: 'REGISTRO_EXITOSO',
    REGISTRO_FALLIDO: 'REGISTRO_FALLIDO'
};

// Estado inicial del contexto de autenticación
const estadoInicial = {
    usuario: null,
    token: null,
    estaAutenticado: false,
    cargando: true,
    error: null,
    tipoAutenticacion: null // 'credenciales', 'google', 'facebook', etc.
};

// Reducer de autenticación con manejo de estados en español
function reductorAuth(estado, accion) {
    switch (accion.type) {
        case ACCIONES_AUTH.INICIO_LOGIN:
            return {
                ...estado,
                cargando: true,
                error: null
            };
            
        case ACCIONES_AUTH.LOGIN_EXITOSO:
            return {
                ...estado,
                usuario: accion.payload.usuario,
                token: accion.payload.token,
                estaAutenticado: true,
                cargando: false,
                error: null,
                tipoAutenticacion: accion.payload.tipoAutenticacion
            };
            
        case ACCIONES_AUTH.LOGIN_FALLIDO:
            return {
                ...estado,
                usuario: null,
                token: null,
                estaAutenticado: false,
                cargando: false,
                error: accion.payload.error,
                tipoAutenticacion: null
            };
            
        case ACCIONES_AUTH.CERRAR_SESION:
            return {
                ...estado,
                usuario: null,
                token: null,
                estaAutenticado: false,
                cargando: false,
                error: null,
                tipoAutenticacion: null
            };
            
        case ACCIONES_AUTH.CARGAR_USUARIO:
            return {
                ...estado,
                usuario: accion.payload.usuario,
                token: accion.payload.token,
                estaAutenticado: !!accion.payload.usuario,
                cargando: false,
                error: null,
                tipoAutenticacion: accion.payload.tipoAutenticacion
            };
            
        case ACCIONES_AUTH.ACTUALIZAR_USUARIO:
            return {
                ...estado,
                usuario: { ...estado.usuario, ...accion.payload },
                cargando: false
            };
            
        case ACCIONES_AUTH.INICIO_REGISTRO:
            return {
                ...estado,
                cargando: true,
                error: null
            };
            
        case ACCIONES_AUTH.REGISTRO_EXITOSO:
            return {
                ...estado,
                usuario: accion.payload.usuario,
                token: accion.payload.token,
                estaAutenticado: true,
                cargando: false,
                error: null,
                tipoAutenticacion: 'credenciales'
            };
            
        case ACCIONES_AUTH.REGISTRO_FALLIDO:
            return {
                ...estado,
                usuario: null,
                token: null,
                estaAutenticado: false,
                cargando: false,
                error: accion.payload.error,
                tipoAutenticacion: null
            };
            
        default:
            return estado;
    }
}

// Contexto de autenticación
const AuthContext = createContext();

// Provider de autenticación
/**
 * Proveedor del contexto de autenticación EXCLUSIVO para usuarios públicos
 * 
 * IMPORTANTE: Este contexto maneja SOLO:
 * - Usuarios de publico.usuarios (clientes/compradores)
 * - Autenticación social y por credenciales públicas
 * - Funcionalidades de e-commerce (carrito, perfil, pedidos)
 * 
 * NO maneja:
 * - credenciales.usuarios_internos (sistema administrativo)
 * - Acceso a /app/sistema/*
 * - Funcionalidades administrativas
 */
export function AuthProvider({ children }) {
    const [estado, dispatch] = useReducer(reductorAuth, estadoInicial);
    
    // Cargar sesión desde localStorage al inicializar
    useEffect(() => {
        // Cargar sesión desde localStorage y validar con /api/auth/me
        const iniciarSesionDesdeAlmacenamiento = async () => {
            try {
                const token = localStorage.getItem('auth_token');
                if (!token) {
                    dispatch({
                        type: ACCIONES_AUTH.CARGAR_USUARIO,
                        payload: { usuario: null, token: null }
                    });
                    return;
                }

                // Validar sesión y obtener perfil
                const me = await authService.me();
                if (me?.authenticated && me?.user) {
                    dispatch({
                        type: ACCIONES_AUTH.CARGAR_USUARIO,
                        payload: { usuario: me.user, token, tipoAutenticacion: 'jwt' }
                    });
                } else {
                    // Sesión inválida
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user_data');
                    dispatch({
                        type: ACCIONES_AUTH.CARGAR_USUARIO,
                        payload: { usuario: null, token: null }
                    });
                }
            } catch (error) {
                // En caso de 401/403
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user_data');
                dispatch({
                    type: ACCIONES_AUTH.CARGAR_USUARIO,
                    payload: { usuario: null, token: null }
                });
            }
        };

        iniciarSesionDesdeAlmacenamiento();
    }, []);
    
    // Login real contra \api\auth\login
    const login = async ({ email, password }) => {
        dispatch({ type: ACCIONES_AUTH.INICIO_LOGIN });

        try {
            const response = await authService.login(email, password);
            if (response?.success) {
                const { user, token } = response;
                // Persistir
                localStorage.setItem('auth_token', token);
                localStorage.setItem('user_data', JSON.stringify(user));

                dispatch({
                    type: ACCIONES_AUTH.LOGIN_EXITOSO,
                    payload: { usuario: user, token, tipoAutenticacion: 'credenciales' }
                });

                toast.success('Inicio de sesión exitoso');
                return { success: true, user, token };
            }

            // Manejo de estados especiales: 401/403/423 si vienen en message
            const message = response?.message || 'Credenciales inválidas';
            throw new Error(message);
        } catch (error) {
            dispatch({
                type: ACCIONES_AUTH.LOGIN_FALLIDO,
                payload: { error: error.message }
            });
            toast.error(error.message);
            return { success: false, error: error.message };
        }
    };
    
    // Registro real contra \api\auth\register
    const register = async ({ nombre, apellido, email, telefono, contrasenia }) => {
        dispatch({ type: ACCIONES_AUTH.INICIO_REGISTRO });

        try {
            const response = await authService.register({ nombre, apellido, email, telefono, contrasenia });
            if (response?.success) {
                const { user, token } = response;
                localStorage.setItem('auth_token', token);
                localStorage.setItem('user_data', JSON.stringify(user));

                dispatch({
                    type: ACCIONES_AUTH.REGISTRO_EXITOSO,
                    payload: { usuario: user, token }
                });
                toast.success('Registro exitoso');
                return { success: true, user, token };
            }
            throw new Error(response?.message || 'Error de registro');
        } catch (error) {
            dispatch({
                type: ACCIONES_AUTH.REGISTRO_FALLIDO,
                payload: { error: error.message }
            });
            toast.error(error.message);
            return { success: false, error: error.message };
        }
    };

    // Login social (Google/Facebook) pasando token del proveedor
    const socialLogin = async (provider, providerToken) => {
        dispatch({ type: ACCIONES_AUTH.INICIO_LOGIN });
        try {
            const response = await authService.socialLogin(provider, providerToken);

            // soportar respuestas { success, user, token } o { user, token }
            const { user, token, success } = response || {};
            if (success || (user && token)) {
                localStorage.setItem('auth_token', token);
                localStorage.setItem('user_data', JSON.stringify(user));
                dispatch({
                    type: ACCIONES_AUTH.LOGIN_EXITOSO,
                    payload: { usuario: user, token, tipoAutenticacion: provider.toLowerCase() }
                });
                toast.success(`Inicio de sesión con ${provider} exitoso`);
                return { success: true, user, token };
            }

            throw new Error(response?.message || `Error al iniciar sesión con ${provider}`);
        } catch (error) {
            dispatch({
                type: ACCIONES_AUTH.LOGIN_FALLIDO,
                payload: { error: error.message }
            });
            toast.error(error.message);
            return { success: false, error: error.message };
        }
    };
    
    // Logout real contra \api\auth\logout
    const logout = async () => {
        try {
            await authService.logout();
            dispatch({ type: ACCIONES_AUTH.CERRAR_SESION });
            toast.success('Sesión cerrada correctamente');
        } catch (error) {
            dispatch({ type: ACCIONES_AUTH.CERRAR_SESION });
            toast.error('Error al cerrar sesión');
        }
    };
    
    // Función para actualizar usuario
    const updateUser = (userData) => {
        const updatedUser = { ...estado.usuario, ...userData };
        
        // Actualizar localStorage
        localStorage.setItem('wins-user', JSON.stringify(updatedUser));
        
        dispatch({
            type: ACCIONES_AUTH.ACTUALIZAR_USUARIO,
            payload: updatedUser
        });
    };
    
    // Verificar si el token es válido
    const isTokenValid = () => {
        if (!estado.token) return false;
        
        try {
            // Aquí podrías decodificar el JWT y verificar la expiración
            // Por ahora solo verificamos que exista
            return true; // eslint-disable-line no-unreachable
        } catch (error) {
            return false;
        }
    };
    
    const value = {
        ...estado,
        // Aliases para compatibilidad
        isAuthenticated: estado.estaAutenticado,
        user: estado.usuario,
        loading: estado.cargando,
        error: estado.error,
        // Funciones
        login,
        register,
        logout,
        socialLogin,
        updateUser,
        isTokenValid
    };
    
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// Hook para usar el contexto de autenticación
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

// Funciones simuladas para desarrollo
async function simulateLogin(credentials) {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Validaciones básicas
    if (!credentials.email || !credentials.password) {
        return {
            success: false,
            message: 'Email y contraseña son requeridos'
        };
    }
    
    // Simular diferentes respuestas
    if (credentials.email === 'error@test.com') {
        return {
            success: false,
            message: 'Credenciales inválidas'
        };
    }
    
    // Simular login exitoso
    return {
        success: true,
        data: {
            user: {
                id: Math.random().toString(36).substr(2, 9),
                email: credentials.email,
                name: credentials.email.split('@')[0],
                firstName: 'Usuario',
                lastName: 'Demo',
                phone: '+51 999 999 999',
                avatar: null,
                emailVerified: true,
                createdAt: new Date().toISOString()
            },
            token: 'demo-jwt-token-' + Math.random().toString(36).substr(2, 9)
        }
    };
}

async function simulateRegister(userData) {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Validaciones básicas
    if (!userData.email || !userData.password || !userData.firstName) {
        return {
            success: false,
            message: 'Todos los campos obligatorios deben ser completados'
        };
    }
    
    if (userData.password !== userData.confirmPassword) {
        return {
            success: false,
            message: 'Las contraseñas no coinciden'
        };
    }
    
    // Simular email ya registrado
    if (userData.email === 'existing@test.com') {
        return {
            success: false,
            message: 'Este email ya está registrado'
        };
    }
    
    // Simular registro exitoso
    return {
        success: true,
        data: {
            user: {
                id: Math.random().toString(36).substr(2, 9),
                email: userData.email,
                name: `${userData.firstName} ${userData.lastName}`,
                firstName: userData.firstName,
                lastName: userData.lastName,
                phone: userData.phone || null,
                avatar: null,
                emailVerified: false,
                createdAt: new Date().toISOString()
            },
            token: 'demo-jwt-token-' + Math.random().toString(36).substr(2, 9)
        }
    };
}

export default AuthContext;
